import { ACTIVITY_CATEGORY_COLORS } from '@/components/Timeline/activityCategories';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { CreateTaskForm } from '@/pages/PatientTimelinePage/TasksManagement/CreateTaskForm';
import { getPatientName } from '@/pages/PatientTimelinePage/services/timelinePatient';
import { alert } from '@/services/alert';
import { ActionIcon, Box, Card, Group, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { createReference } from '@medplum/core';
import { IconCheckbox, IconX } from '@tabler/icons-react';
import { ReactElement, useMemo } from 'react';

/**
 * Create a task from the timeline's Add menu, alongside sticky note / SMS /
 * chat — the same composer slot every other Add item uses.
 *
 * The body is the patient tab's CreateTaskForm, so the patient has exactly
 * one task form no matter which entry point opened it. Nothing refreshes the
 * Tasks tab here on purpose: useClinicalTasks subscribes, so the new task
 * arrives on the tab (and its status dot) the same way a teammate's would.
 */
export function TaskMessageBox(): ReactElement | null {
  const { patientContext, messageBoxContext } = useTimelineContext();
  const patient = patientContext?.patient;
  const { setShowMessageInput } = messageBoxContext;

  const patientName = useMemo(() => (patient ? getPatientName(patient) : 'Unknown Patient'), [patient]);

  if (!patient) {
    return null;
  }

  return (
    <Card withBorder padding={0} radius="lg">
      <Box style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
        <Group w="100%" p="xs" justify="space-between" align="center">
          <Group>
            {/* Mustard, the task marker colour everywhere else in the timeline. */}
            <ThemeIcon variant="filled" radius="lg" size="lg" bg={ACTIVITY_CATEGORY_COLORS.notes}>
              <IconCheckbox size={16} />
            </ThemeIcon>
            <Text variant="header" c={ACTIVITY_CATEGORY_COLORS.notes}>
              Add task for {patientName}
            </Text>
          </Group>
          <Tooltip label="Close" withArrow position="top-end" openDelay={500}>
            <ActionIcon variant="subtle" color="gray" aria-label="Close" onClick={() => setShowMessageInput(false)}>
              <IconX size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Box>

      <Box p="xs">
        <CreateTaskForm
          patient={createReference(patient)}
          onCancel={() => setShowMessageInput(false)}
          onSave={(task) => {
            setShowMessageInput(false);
            alert.success({ title: 'Task created', message: task.description ?? '' });
          }}
        />
      </Box>
    </Card>
  );
}
